import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { motion, Reorder, AnimatePresence } from 'framer-motion';
import CustomDomain from './CustomDomain';
import { Link } from 'react-router-dom';

export default function EditProfile() {
  const { profile, updateProfile, updateAuth, uploadFile, user } = useAppContext();
  const [activeTab, setActiveTab] = useState('account');
  const [formData, setFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState({ avatar: false, banner: false, background: false, portfolio: false });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  
  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
  const [isUpdatingPass, setIsUpdatingPass] = useState(false);

  const ALL_SECTIONS = ['websites', 'contact', 'socials', 'music', 'videos', 'photos'];
  const isPro = profile?.subscription_status === 'active' || profile?.is_lifetime;

  useEffect(() => {
    if (profile) {
      let existingOrder = profile.section_order || ALL_SECTIONS;
      const mergedOrder = [...existingOrder];
      ALL_SECTIONS.forEach(s => { if (!mergedOrder.includes(s)) mergedOrder.push(s); });
      
      setFormData({
        ...profile,
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        username: profile.username || '',
        title: profile.title || '',
        company: profile.company || '',
        contact_email: profile.contact_email || '',
        phone_mobile: profile.phone_mobile || '',
        phone_business: profile.phone_business || '',
        address_line1: profile.address_line1 || '',
        address_line2: profile.address_line2 || '',
        city: profile.city || '',
        state: profile.state || '',
        zip: profile.zip || '',
        custom_websites: profile.custom_websites || [],
        youtube_videos: profile.youtube_videos || [],
        portfolio_photos: profile.portfolio_photos || [],
        social: profile.social || {},
        music_links: profile.music_links || {},
        section_order: mergedOrder,
        hide_footer: profile.hide_footer || false,
        meta_title: profile.meta_title || '',
        page_background_url: profile.page_background_url || ''
      });
    }
  }, [profile]);

  const handleFileUpload = async (e, type) => {
    if ((type === 'banner' || type === 'portfolio' || type === 'background') && !isPro) return;
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(prev => ({ ...prev, [type]: true }));
    try {
      const { url, error: uploadError } = await uploadFile('profile-assets', file, user.id);
      if (uploadError) throw uploadError;
      if (type === 'portfolio') {
        const newPhoto = { id: Date.now().toString(), url };
        setFormData(prev => ({ ...prev, portfolio_photos: [...(prev.portfolio_photos || []), newPhoto] }));
      } else {
        const fieldMap = { avatar: 'avatar_url', banner: 'banner_url', background: 'page_background_url' };
        setFormData(prev => ({ ...prev, [fieldMap[type]]: url }));
      }
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (passwords.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsUpdatingPass(true);
    setError('');
    try {
      const { error: authErr } = await updateAuth({ password: passwords.newPassword });
      if (authErr) throw authErr;
      setPasswords({ newPassword: '', confirmPassword: '' });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpdatingPass(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;
    setIsSaving(true);
    setError('');
    try {
      const { error: profileError } = await updateProfile(formData);
      if (profileError) throw profileError;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!formData) return null;

  const tabs = [
    { id: 'account', label: 'Account', icon: 'User' },
    { id: 'design', label: 'Design', icon: 'Layout' },
    { id: 'modules', label: 'Modules', icon: 'Grid' },
    { id: 'portfolio', label: 'Portfolio', icon: 'Image', isPro: true },
    { id: 'domain', label: 'Domain', icon: 'Globe', isPro: true },
    { id: 'sort', label: 'Reorder', icon: 'Move' },
  ];

  return (
    <DashboardLayout>
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Profile</h1>
          <p className="text-gray-500 mt-1">Customize how the world sees you.</p>
        </div>
        {!isPro && (
          <Link to="/pricing" className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl text-sm font-bold border border-amber-100 hover:bg-amber-100 transition-colors">
            <SafeIcon name="Star" /> Upgrade to Pro
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-100 pb-4">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all relative ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white text-gray-500 hover:bg-gray-50 border border-gray-100'}`}>
            <SafeIcon name={tab.icon} size={16} />
            {tab.label}
            {tab.isPro && !isPro && <span className="ml-1 text-[8px] bg-amber-400 text-white px-1 py-0.5 rounded-md uppercase tracking-tighter shadow-sm">Pro</span>}
          </button>
        ))}
      </div>

      <div className="max-w-4xl pb-32">
        {/* Account Tab (Independent Forms) */}
        {activeTab === 'account' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2"><SafeIcon name="User" /> Identity</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Profile Username</label>
                      <div className="relative">
                        <span style={{ color: 'rgb(156, 163, 175)' }} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">tapbadge.app/p/</span>
                        <input value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })} className="w-full pl-32 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-medium text-sm transition-all shadow-sm" />
                      </div>
                    </div>
                  </div>
                  <Input label="First Name" value={formData.first_name} onChange={v => setFormData({ ...formData, first_name: v })} />
                  <Input label="Last Name" value={formData.last_name} onChange={v => setFormData({ ...formData, last_name: v })} />
                  <Input label="Job Title" value={formData.title} onChange={v => setFormData({ ...formData, title: v })} />
                  <Input label="Company" value={formData.company} onChange={v => setFormData({ ...formData, company: v })} />
                </div>
              </div>

              <div className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 space-y-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2"><SafeIcon name="Phone" /> Contact & Location</h3>
                <div className="space-y-1">
                  <Input label="Profile Email" type="email" placeholder="you@company.com" value={formData.contact_email} onChange={v => setFormData({ ...formData, contact_email: v })} />
                  <p className="text-[10px] text-gray-400 ml-1">Shown on your card. This doesn't change the email you log in with ({user?.email}). Leave it blank to hide it.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="Mobile Phone" value={formData.phone_mobile} onChange={v => setFormData({ ...formData, phone_mobile: v })} />
                  <Input label="Business Phone" value={formData.phone_business} onChange={v => setFormData({ ...formData, phone_business: v })} />
                </div>
                <div className="space-y-4">
                  <Input label="Address Line 1" value={formData.address_line1} onChange={v => setFormData({ ...formData, address_line1: v })} />
                  <Input label="Address Line 2" value={formData.address_line2} onChange={v => setFormData({ ...formData, address_line2: v })} />
                  <div className="grid grid-cols-3 gap-4">
                    <Input label="City" value={formData.city} onChange={v => setFormData({ ...formData, city: v })} />
                    <Input label="State" value={formData.state} onChange={v => setFormData({ ...formData, state: v })} />
                    <Input label="Zip" value={formData.zip} onChange={v => setFormData({ ...formData, zip: v })} />
                  </div>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full rounded-2xl py-4 shadow-xl shadow-indigo-100" disabled={isSaving}>
                {isSaving ? 'Saving Changes...' : 'Save Profile Data'}
              </Button>
            </form>

            <div className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 space-y-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2"><SafeIcon name="Lock" /> Security</h3>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">New Password</label>
                    <input type="password" value={passwords.newPassword} onChange={e => setPasswords({ ...passwords, newPassword: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm" placeholder="••••••••" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Confirm New Password</label>
                    <input type="password" value={passwords.confirmPassword} onChange={e => setPasswords({ ...passwords, confirmPassword: e.target.value })} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white text-sm" placeholder="••••••••" />
                  </div>
                </div>
                <Button type="submit" variant="secondary" className="w-full" disabled={isUpdatingPass || !passwords.newPassword}>
                  {isUpdatingPass ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Domain Tab (Independent Component / Self-Contained Form) */}
        {activeTab === 'domain' && <CustomDomain />}

        {/* Bulk Update Form for Design/Modules/Portfolio/Sort */}
        {(activeTab === 'design' || activeTab === 'modules' || activeTab === 'portfolio' || activeTab === 'sort') && (
          <form onSubmit={handleSubmit}>
            {activeTab === 'design' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
                <UploadField label="Profile Picture" type="avatar" url={formData.avatar_url} onUpload={handleFileUpload} loading={isUploading.avatar} />
                <UploadField label="Profile Banner" type="banner" url={formData.banner_url} onUpload={handleFileUpload} loading={isUploading.banner} isProRequired={true} isPro={isPro} />
                <UploadField label="Page Background" type="background" url={formData.page_background_url} onUpload={handleFileUpload} loading={isUploading.background} isProRequired={true} isPro={isPro} />
                
                <div className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2"><SafeIcon name="Search" /> SEO Settings</h3>
                      <p className="text-xs text-gray-500 mt-1">Customize how your profile appears in search results and browser tabs.</p>
                    </div>
                    {!isPro && <span className="px-3 py-1 bg-amber-500 text-white text-[10px] font-bold uppercase rounded-full tracking-widest shadow-sm">Pro Only</span>}
                  </div>
                  <div className={!isPro ? 'opacity-60 grayscale cursor-not-allowed' : ''}>
                    <Input 
                      label="Browser Tab Title" 
                      placeholder="e.g. John Smith | Creative Director" 
                      value={formData.meta_title} 
                      onChange={v => setFormData({ ...formData, meta_title: v })} 
                      disabled={!isPro}
                    />
                    <p className="text-[10px] text-gray-400 mt-2 ml-1">This replaces the default "Name | TapBadge" in the browser tab.</p>
                  </div>
                </div>

                <div className="p-8 bg-indigo-50 border border-indigo-100 rounded-[2.5rem] space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">White Labeling</h3>
                      <p className="text-sm text-gray-500">Remove branding and customize browser settings</p>
                    </div>
                    {!isPro && <span className="px-3 py-1 bg-amber-500 text-white text-[10px] font-bold uppercase rounded-full tracking-widest shadow-sm">Pro Only</span>}
                  </div>
                  <div className={`space-y-4 ${!isPro ? 'opacity-60 grayscale' : ''}`}>
                    <label className={`flex items-center justify-between p-5 bg-white rounded-2xl border transition-all ${!isPro ? 'cursor-not-allowed border-gray-200' : 'hover:border-indigo-300 cursor-pointer shadow-sm'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${formData.hide_footer ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-400'}`}><SafeIcon name="EyeOff" /></div>
                        <div><p className="font-bold text-gray-900 text-sm">Hide "Powered by TapBadge"</p></div>
                      </div>
                      <input type="checkbox" disabled={!isPro} checked={formData.hide_footer} onChange={(e) => setFormData({ ...formData, hide_footer: e.target.checked })} className="w-6 h-6 rounded-lg text-indigo-600" />
                    </label>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'modules' && (
              <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2">
                <ModuleSection title="Social Media Handles">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {['instagram', 'linkedin', 'twitter', 'facebook', 'youtube', 'tiktok'].map(p => (
                      <SocialInput key={p} platform={p} value={formData.social?.[p]} onChange={v => setFormData({ ...formData, social: { ...formData.social, [p]: v } })} />
                    ))}
                  </div>
                </ModuleSection>

                <ModuleSection title="Music Profiles" icon="Music" isProRequired={true} isPro={isPro}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {['spotify', 'apple_music', 'soundcloud', 'youtube_music'].map(p => (
                      <SocialInput key={p} platform={p.replace('_', ' ')} value={formData.music_links?.[p]} onChange={v => setFormData({ ...formData, music_links: { ...formData.music_links, [p]: v } })} />
                    ))}
                  </div>
                </ModuleSection>

                <ModuleSection title="YouTube Portfolio" icon="Video">
                  <div className="space-y-4">
                    {formData.youtube_videos.map((video, i) => (
                      <div key={i} className="flex gap-3 bg-white p-4 border border-gray-100 rounded-2xl shadow-sm items-center">
                        <div className="w-10 h-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center"><SafeIcon name="Youtube" /></div>
                        <input placeholder="YouTube Video URL" value={video.url} onChange={e => { const updated = [...formData.youtube_videos]; updated[i].url = e.target.value; setFormData({ ...formData, youtube_videos: updated }); }} className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-sm" />
                        <button type="button" onClick={() => setFormData({ ...formData, youtube_videos: formData.youtube_videos.filter((_, idx) => idx !== i) })} className="text-gray-300 hover:text-red-500 transition-colors"><SafeIcon name="Trash2" size={18} /></button>
                      </div>
                    ))}
                    <Button type="button" variant="secondary" size="sm" onClick={() => setFormData({ ...formData, youtube_videos: [...formData.youtube_videos, { url: '', title: '' }] })}>Add Video</Button>
                  </div>
                </ModuleSection>

                <ModuleSection title="Custom Website Links">
                  <div className="space-y-3">
                    {formData.custom_websites.map((site, i) => (
                      <div key={i} className="flex gap-3 bg-white p-4 border border-gray-100 rounded-2xl shadow-sm items-center">
                        <input placeholder="Label (e.g. My Blog)" value={site.label} onChange={e => { const updated = [...formData.custom_websites]; updated[i].label = e.target.value; setFormData({ ...formData, custom_websites: updated }); }} className="w-1/3 bg-gray-50 px-3 py-2 rounded-lg text-sm" />
                        <input placeholder="URL" value={site.url} onChange={e => { const updated = [...formData.custom_websites]; updated[i].url = e.target.value; setFormData({ ...formData, custom_websites: updated }); }} className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-sm" />
                        <button type="button" onClick={() => setFormData({ ...formData, custom_websites: formData.custom_websites.filter((_, idx) => idx !== i) })} className="text-gray-300 hover:text-red-500 transition-colors"><SafeIcon name="Trash2" size={18} /></button>
                      </div>
                    ))}
                    <Button type="button" variant="secondary" size="sm" onClick={() => setFormData({ ...formData, custom_websites: [...formData.custom_websites, { label: '', url: '' }] })}>Add Website Link</Button>
                  </div>
                </ModuleSection>
              </div>
            )}

            {activeTab === 'portfolio' && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
                <div className="p-8 bg-gray-50 rounded-[2.5rem] border border-gray-100">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Photo Portfolio</h2>
                      <p className="text-sm text-gray-500">Showcase your work directly on your profile.</p>
                    </div>
                    {!isPro && <span className="px-4 py-1.5 bg-amber-500 text-white text-[10px] font-bold uppercase rounded-full tracking-widest shadow-lg">Pro Feature</span>}
                  </div>

                  <div className={`space-y-6 ${!isPro ? 'opacity-60 pointer-events-none' : ''}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {formData.portfolio_photos?.map(photo => (
                        <div key={photo.id} className="relative aspect-square rounded-2xl overflow-hidden border border-gray-200 group">
                          <img src={photo.url} className="w-full h-full object-cover" alt="" />
                          <button type="button" onClick={() => setFormData({ ...formData, portfolio_photos: formData.portfolio_photos.filter(p => p.id !== photo.id) })} className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                            <SafeIcon name="Trash2" size={14} />
                          </button>
                        </div>
                      ))}
                      <label className="aspect-square rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-white transition-all cursor-pointer group">
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'portfolio')} disabled={isUploading.portfolio} />
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                          {isUploading.portfolio ? <SafeIcon name="Loader" className="animate-spin" /> : <SafeIcon name="Plus" />}
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Add Photo</span>
                      </label>
                    </div>
                  </div>

                  {!isPro && (
                    <div className="mt-10 p-6 bg-white rounded-3xl border border-amber-100 text-center shadow-sm">
                      <p className="text-amber-800 font-bold mb-4">Unlock visual storytelling with a Portfolio.</p>
                      <Link to="/pricing">
                        <Button variant="primary" className="bg-amber-500 hover:bg-amber-600 border-none shadow-lg shadow-amber-100">Upgrade to Pro</Button>
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'sort' && (
              <div className="p-6 bg-indigo-50 rounded-[2.5rem] border border-indigo-100 animate-in fade-in slide-in-from-bottom-2">
                <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-widest mb-6 px-2 flex items-center gap-2">
                  <SafeIcon name="Move" /> Arrange Profile Sections
                </h3>
                <Reorder.Group axis="y" values={formData.section_order} onReorder={o => setFormData({ ...formData, section_order: o })} className="space-y-3">
                  {formData.section_order.map((section) => (
                    <Reorder.Item key={section} value={section} className="p-5 bg-white border border-gray-200 rounded-2xl shadow-sm flex items-center gap-4 cursor-grab active:cursor-grabbing hover:border-indigo-300 transition-colors">
                      <SafeIcon name="Menu" className="text-gray-300" />
                      <span className="font-bold text-gray-900 flex-1 capitalize">{section}</span>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              </div>
            )}

            {/* Sticky Save Bar (only for Design, Modules, Portfolio, Sort) */}
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-lg px-4 z-50">
              <div className="bg-white/90 backdrop-blur-xl p-4 rounded-[2rem] border border-gray-200 shadow-2xl flex flex-col gap-2">
                {error && <p className="text-center text-red-500 text-xs font-bold mb-1">{error}</p>}
                <div className="flex items-center gap-4">
                  <Button type="submit" size="lg" className="flex-1 rounded-2xl py-4 shadow-xl shadow-indigo-100" disabled={isSaving}>
                    {isSaving ? 'Saving Changes...' : 'Save Profile Settings'}
                  </Button>
                  {saved && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon name="Check" size={24} />
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          </form>
        )}
        
        {/* Overlay notification for saves */}
        {saved && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60]">
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-green-500 text-white px-6 py-3 rounded-full shadow-2xl font-bold flex items-center gap-2">
              <SafeIcon name="Check" /> Changes Saved Successfully
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const Input = ({ label, value, onChange, ...props }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">{label}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none bg-white font-medium text-sm transition-all shadow-sm disabled:bg-gray-100 disabled:text-gray-400" {...props} />
  </div>
);

const UploadField = ({ label, type, url, onUpload, loading, isProRequired = false, isPro = true }) => (
  <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 flex items-center gap-6 relative overflow-hidden group">
    <div className={`flex-shrink-0 bg-white border border-gray-100 shadow-sm overflow-hidden ${type === 'avatar' ? 'w-20 h-20 rounded-full' : 'w-32 h-20 rounded-xl'}`}>
      {url ? <img src={url} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><SafeIcon name="Image" size={20} /></div>}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2">
        <label className="text-xs font-bold text-gray-400 uppercase">{label}</label>
        {isProRequired && !isPro && <span className="px-2 py-0.5 bg-amber-500 text-white text-[8px] font-bold rounded uppercase tracking-wider shadow-sm">Pro</span>}
      </div>
      <input type="file" accept="image/*" className="hidden" id={`up-${type}`} onChange={e => onUpload(e, type)} disabled={isProRequired && !isPro} />
      <label htmlFor={`up-${type}`} className={`inline-flex items-center px-4 py-2 rounded-xl text-xs font-bold transition-all ${isProRequired && !isPro ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer shadow-sm'}`}>
        {loading ? <SafeIcon name="Loader" className="animate-spin mr-2" /> : <SafeIcon name="Upload" className="mr-2" />}
        {loading ? 'Uploading' : 'Upload Image'}
      </label>
    </div>
    {isProRequired && !isPro && <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px] pointer-events-none" />}
  </div>
);

const ModuleSection = ({ title, icon = "Grid", children, isProRequired = false, isPro = true }) => (
  <section className="space-y-4 relative">
    <div className="flex items-center justify-between px-1">
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2">
        <SafeIcon name={icon} className="text-indigo-600" /> {title}
      </h3>
      {isProRequired && !isPro && <span className="px-2 py-0.5 bg-amber-500 text-white text-[8px] font-bold rounded uppercase tracking-wider shadow-sm">Pro Feature</span>}
    </div>
    <div className={`${isProRequired && !isPro ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
      {children}
    </div>
  </section>
);

const SocialInput = ({ platform, value, onChange }) => (
  <div className="flex items-center gap-3 bg-white p-3 border border-gray-100 rounded-2xl shadow-sm hover:border-indigo-200 transition-colors">
    <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 capitalize text-[10px] font-bold">{platform[0]}</div>
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={`${platform} handle`} className="flex-1 bg-transparent outline-none text-sm font-medium" />
  </div>
);
